// @flow

import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import actionCreators from "@modules/reporting/actionCreator";
import { getAlertDetailsFiltersSchema } from "@modules/reporting/selectors/reports/alert-details";
import type { AddEditModalConfig } from "@modules/reporting/reducer/modals/types";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";
import type {
  DefaultReport,
  MySavedReport,
  AddEditReportModalType,
  SaveReportModalType,
} from "@modules/reporting/reducer/modals/types";
import { getAddEditReportModal, getSaveReportModal } from "@modules/reporting/selectors/modals";
import { split } from "@transformers/schema";
import AddEditReportModal from "../add-edit-report-modal";
import SaveReportModal from "../save-report-modal";
import StepFiltersView from "./step-filters-view";
import config from "@config";

type OwnProps = $Exact<{
  clientId: number,
  returnReportToSave?: (data: MySavedReport) => void,
  getReport?: (data: MySavedReport) => () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  addEditModal: AddEditReportModalType,
  saveReportModal: SaveReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  getAlertDetailsFiltersSchema: typeof actionCreators.GetAlertDetailsFiltersSchema,
  createAlertDetailsReport: typeof actionCreators.CreateAlertDetailsReport,
  updateAlertDetailsReport: typeof actionCreators.UpdateAlertDetailsReport,
  setReportToSave: typeof actionCreators.SetAlertDetailsReportToSave,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

type ComponentState = $Exact<{
  data: Object,
}>;

export class AlertDetailsAddEditReportModal extends React.Component<Props, ComponentState> {
  state = {
    data: {},
  };

  componentDidMount() {
    this.props.getAlertDetailsFiltersSchema({ clientId: this.props.clientId });
  }

  onUpdateAlertDetailsReport = (data: MySavedReport) => {
    if (this.props.returnReportToSave) {
      this.props.returnReportToSave(data);
      this.props.toggleAddEditReportModal({ visible: false });
      return;
    }

    this.props.updateAlertDetailsReport({
      clientId: this.props.clientId,
      reportId: data.id,
      data,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  onCreateAlertDetailsReport = (data: DefaultReport) => {
    this.props.createAlertDetailsReport({
      clientId: this.props.clientId,
      ...data,
      columns: this.props?.columns || data.columns,
    });
  };

  onGetReport = (data: MySavedReport) => {
    if (!this.props.getReport) {
      return;
    }

    this.props.getReport(data)();
    this.props.setReportToSave({
      ...data,
      isNew: true,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  getFiltersView = (data: Object) => {
    return <StepFiltersView data={data} />;
  };

  get filtersSchema() {
    if (!this.props.schema) {
      return {};
    }

    const _schema = split(this.props.schema, [
      "dateRange",
      "fromDate",
      "toDate",
      "absenceType",
      "alertTypes",
      "staffGroups",
      "gender",
      "ageGroups",
      "includeLeavers",
      "includeEmployeesWithDisability",
    ]);

    return _schema;
  }

  get modalConfig(): AddEditModalConfig {
    return [
      {
        type: ReportModalStepTypes.FILTERS,
        title: {
          new: this.props.t("SET_FILTERS"),
          edit: this.props.t("FILTERS"),
        },
        schema: this.filtersSchema,
        uiSchema: this.getStepFiltersUiSchema(this.props.t, config),
        getView: this.getFiltersView,
        hideNextButton: true,
      },
    ];
  }

  get isEditModal(): boolean {
    return !!this.props.addEditModal.data;
  }

  getStepFiltersUiSchema = (t: Function, config: Object) => {
    return {
      // Period
      dateRange: {
        "ui:header": this.isEditModal && t("PERIOD"),
        "ui:options": {
          tooltip: {
            title: t("REPORT_FILTER_DATE_RANGE"),
            description: t("DATE_RANGE_TOOLTIP"),
            id: "alert-details-report-date-range",
            size: "medium",
          },
        },
      },
      fromDate: {
        "ui:title": t("FROM_DATE"),
        "ui:widget": "date-picker",
        "ui:options": { dateFormat: "YYYY-MM-DD" },
      },
      toDate: {
        "ui:title": t("TO_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          width: "20%",
          dateFormat: config.dateFormatBE,
        },
      },
      // Status
      absenceType: {
        "ui:header": this.isEditModal && t("STATUS"),
      },
      alertTypes: {
        items: {
          "ui:options": {
            hidelabel: true,
          },
        },
        "ui:field": "multi-select",
      },
      //Length

      //Reason

      //Emploiyees
      includeLeavers: {
        "ui:header": this.isEditModal && t("EMPLOYEES"),
        "ui:widget": "select",
      },
      includeEmployeesWithDisability: {
        "ui:title": t("INCLUDE_EMPLOYEES_WITH_DISABILITY"),
        "ui:widget": "select",
      },
      staffGroups: {
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
        "ui:field": "multi-select",
      },
      ageGroups: {
        items: {
          "ui:options": {
            hidelabel: true,
          },
        },
        "ui:field": "multi-select",
      },

      "ui:order": this.isEditModal
        ? [
            //  Period
            "dateRange",
            "fromDate",
            "toDate",
            //Status
            "absenceType",
            "alertTypes",

            // Employees
            "includeLeavers",
            "includeEmployeesWithDisability",
            "gender",
            "staffGroups",

            "ageGroups",
            "*",
          ]
        : [
            "dateRange",
            "includeLeavers",
            "fromDate",
            "includeEmployeesWithDisability",
            "toDate",
            "gender",
            "absenceType",
            "staffGroups",
            "alertTypes",
            "ageGroups",
            "*",
          ],
    };
  };

  render() {
    return (
      <React.Fragment>
        {this.props.addEditModal.visible && (
          <AddEditReportModal
            getReport={this.onGetReport}
            initialStep={this.props.addEditModal.initialStep}
            updateReport={this.onUpdateAlertDetailsReport}
            clientId={this.props.clientId}
            modalConfig={this.modalConfig}
            onChange={(data: Object) => {
              this.setState({ data });
            }}
          />
        )}
        {this.props.saveReportModal.visible && (
          <SaveReportModal
            createReport={this.onCreateAlertDetailsReport}
            updateReport={this.onUpdateAlertDetailsReport}
            columns={this.props.columns}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  schema: getAlertDetailsFiltersSchema(state),
  addEditModal: getAddEditReportModal(state),
  saveReportModal: getSaveReportModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAlertDetailsFiltersSchema: actionCreators.GetAlertDetailsFiltersSchema,
  createAlertDetailsReport: actionCreators.CreateAlertDetailsReport,
  updateAlertDetailsReport: actionCreators.UpdateAlertDetailsReport,
  setReportToSave: actionCreators.SetAlertDetailsReportToSave,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AlertDetailsAddEditReportModal);
